import { computed, inject } from '@angular/core';
import {
  signalStore,
  withComputed,
  withMethods,
  withState,
  patchState,
} from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { encodeApiError } from '@sandbox-platform/shared-ui';
import { AttendanceSettingsService } from '../../attendance-settings.service';
import { AttendanceSettingsDTO } from '../../attendance-settings.types';
import { initialState, TSaveAttendanceSettings } from './definitions';

export const AttendanceSettingDialogStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withComputed((store) => ({
    hasSettings: computed(() => store.settings() !== null),
    isLoading: computed(() => store.loading()),
    hasPersistedSettings: computed(() => (store.settings()?.version ?? 0) > 0),
  })),

  withMethods((store, apiService = inject(AttendanceSettingsService)) => ({
    async get(): Promise<AttendanceSettingsDTO | null> {
      patchState(store, { loading: true, error: null });

      try {
        const response = await firstValueFrom(apiService.get());
        if (response?.data) {
          patchState(store, { settings: response.data });
          return response.data;
        }

        patchState(store, { error: 'No attendance settings data received' });
        return null;
      } catch (error) {
        patchState(store, {
          error: encodeApiError(error, 'Failed to load attendance settings'),
        });
        return null;
      } finally {
        patchState(store, { loading: false });
      }
    },

    async save(
      payload: TSaveAttendanceSettings
    ): Promise<AttendanceSettingsDTO | null> {
      patchState(store, { loading: true, error: null });

      try {
        const response = payload.useUpdate
          ? await firstValueFrom(apiService.update(payload.updateDto))
          : await firstValueFrom(apiService.create(payload.createDto));

        if (response?.data) {
          patchState(store, { settings: response.data });
          payload.afterSave(response.data);
          return response.data;
        }

        patchState(store, {
          error: 'No attendance settings data received from save',
        });
        return null;
      } catch (error) {
        patchState(store, {
          error: encodeApiError(error, 'Failed to save attendance settings'),
        });
        return null;
      } finally {
        patchState(store, { loading: false });
      }
    },

    setSettings(settings: AttendanceSettingsDTO | null): void {
      patchState(store, { settings });
    },

    clearError(): void {
      patchState(store, { error: null });
    },
  }))
);

export type AttendanceSettingDialogStoreType = InstanceType<
  typeof AttendanceSettingDialogStore
>;
